import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, createHashRouter, Navigate } from 'react-router'

import { env } from '@/config/env'
import { EventsPage } from '@/pages/events/EventsPage'
import { HomePage } from '@/pages/home/HomePage'
import { AppLayout } from '@/shared/layout/AppLayout'

import { ROUTES } from './paths'
import { ProtectedRoute } from './ProtectedRoute'
import { DEFAULT_ROUTE, ENABLED_SECTIONS, isSectionEnabled, type SectionKey } from './sections'

/**
 * «Главная» и «События» грузятся сразу (обе без графиков), остальные — лениво при первом
 * заходе: их код и Chart.js не попадают в первый бандл. Suspense-заглушка — в AppLayout вокруг Outlet.
 */
const ClientsPage = lazy(() =>
  import('@/pages/clients/ClientsPage').then((m) => ({ default: m.ClientsPage })),
)
const CarsPage = lazy(() => import('@/pages/cars/CarsPage').then((m) => ({ default: m.CarsPage })))
const LoginPage = lazy(() =>
  import('@/pages/login/LoginPage').then((m) => ({ default: m.LoginPage })),
)
const NotFoundPage = lazy(() =>
  import('@/pages/not-found/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
)

const SECTION_ROUTES: Record<SectionKey, { path: string; element: ReactNode }> = {
  home: { path: ROUTES.home, element: <HomePage /> },
  clients: { path: ROUTES.clients, element: <ClientsPage /> },
  cars: { path: ROUTES.cars, element: <CarsPage /> },
  events: { path: ROUTES.events, element: <EventsPage /> },
}

const createRouter = env.routerMode === 'hash' ? createHashRouter : createBrowserRouter

export const router = createRouter([
  {
    path: ROUTES.login,
    element: (
      <Suspense fallback={null}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // Пока главная выключена, корень ведёт в первый раздел меню.
          ...(isSectionEnabled('home')
            ? []
            : [{ path: ROUTES.home, element: <Navigate to={DEFAULT_ROUTE} replace /> }]),
          ...ENABLED_SECTIONS.map((key) => SECTION_ROUTES[key]),
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={null}>
        <NotFoundPage />
      </Suspense>
    ),
  },
])
